// "IS IT DONE?" in ONE read — the task's durable status, its run, its pull
// request, the CI / review / deploy facts about that PR and the open blocker,
// folded under one `phase` a conversational client reads at a glance (#3631).
// See `../task.ts` for the file docblock.

import { z } from 'zod';
import type { DeployVerdictSource } from '../../value-objects/deploy-verdict';
import type { BlockerId, TaskId } from '../../value-objects/ids';
import type { TaskOrigin } from '../../value-objects/task-envelope';
import type { BlockerKind } from '../blocker';
import type { CiStatus, MergeDecision, PullRequestState } from '../issue';
import type { TaskFailureKind } from './failure-kind';
import type { TaskHoldReason } from './hold';
import { ORIGIN_RUN_AUTHORS_PLAN } from './origin-traits';
import type { OutcomeKind } from './outcome';
import { TaskStatus as Status, type TaskStatus } from './status';

/**
 * The phase a chat client reads first. Before this a caller needed FOUR calls to
 * answer "is it done?" — `task run_status`, `task result`, `pr list`, `status` —
 * and had to know which status names mean "the PR is open and CI is red" versus
 * "the run is still going". This vocabulary says it in one word.
 *
 * Every member but {@link Blocked} is a total projection of {@link TaskStatus}
 * ({@link taskOutcomePhase} — a `switch` with no default, so a status added to the
 * enum fails typecheck until it is phased). {@link Blocked} comes from a
 * different axis: an OPEN `blockers` row naming the task. A blocker is raised
 * mid-run and the run then ENDS (the scout returns, the devops lane settles), so
 * the row's own status says `failed` or `delivered` while the truthful answer is
 * "stopped to ask you something, and nobody has answered". {@link resolveTaskOutcomePhase}
 * states the precedence in one place.
 *
 * A READ-MODEL PROJECTION, like {@link TaskLifecycleBucket}: the durable `status`
 * stays the authority and rides beside `phase` on every outcome; nothing
 * dispatches off a phase.
 */
export const TaskOutcomePhase = {
  /**
   * `pending` — not claimed yet; `holdReason` says why when a hold is stamped, and
   * `waitingOn` names the unreleased dependencies the claim gates on (no hold writer stamps those).
   */
  Queued: 'queued',
  /** `assigned` / `in_progress` — a box holds it; `run.runId` is null until the first event lands. */
  Running: 'running',
  /**
   * `completed` — the run finished and delivered: a PR opened (`pr`), a report
   * or an ops action landed in run events (`report` / `ops_action`). For a `pr`
   * task the babysit watch hops it to {@link AwaitingCi} next; for the other two
   * kinds this IS done.
   */
  Delivered: 'delivered',
  /** `awaiting_ci` — PR open, CI not yet green (checks pending, or a red PR being self-fixed). */
  AwaitingCi: 'awaiting_ci',
  /** `awaiting_review` — CI green, gating on review / comments / mergeability. */
  InReview: 'in_review',
  /** `mergeable` — every gate green; auto-merge fires or a human merges. */
  AwaitingMerge: 'awaiting_merge',
  /**
   * `merged` — the task's status, or its linked PR's state, says
   * merged. Terminal unless `deploy` is `pending`: `deploy` says where the change is past
   * the merge ({@link TaskOutcomeDeploy}). `priorFailure` the
   * attempt that failed before the PR merged anyway.
   */
  Merged: 'merged',
  /** `failed` — terminal; `failure` carries the kind and the terminal reason text. */
  Failed: 'failed',
  /** `cancelled` — terminal, by an operator or by policy. */
  Cancelled: 'cancelled',
  /** An OPEN blocker names this task — a human owes an answer before anything moves. */
  Blocked: 'blocked',
} as const;
export const TaskOutcomePhaseSchema = z.enum(TaskOutcomePhase);
export type TaskOutcomePhase = z.infer<typeof TaskOutcomePhaseSchema>;

/** The status-derived phases: every member but {@link TaskOutcomePhase.Blocked}. */
export type TaskStatusPhase = Exclude<TaskOutcomePhase, typeof TaskOutcomePhase.Blocked>;

/**
 * `TaskStatus` → its phase. TOTAL over the enum — a `switch` with no default, the
 * {@link taskLifecycleBucket} posture — so a new status cannot reach a chat
 * client as `undefined`. `entities/task-outcome.test.ts` walks `TaskStatusSchema.options`
 * and asserts every one lands on exactly one member.
 */
export function taskOutcomePhase(status: TaskStatus): TaskStatusPhase {
  switch (status) {
    case Status.Pending:
      return TaskOutcomePhase.Queued;
    case Status.Assigned:
    case Status.InProgress:
    // #6747: `Running`, never `Queued` — `getTaskOutcome`'s `Queued` arm assumes a
    // `pending` row (queue position and depth); its `Running` arm passes a null run through.
    case Status.Paused:
      return TaskOutcomePhase.Running;
    case Status.Completed:
      return TaskOutcomePhase.Delivered;
    case Status.AwaitingCi:
      return TaskOutcomePhase.AwaitingCi;
    case Status.AwaitingReview:
      return TaskOutcomePhase.InReview;
    case Status.Mergeable:
      return TaskOutcomePhase.AwaitingMerge;
    case Status.Merged:
      return TaskOutcomePhase.Merged;
    case Status.Failed:
      return TaskOutcomePhase.Failed;
    case Status.Cancelled:
      return TaskOutcomePhase.Cancelled;
  }
}

/**
 * The precedence between the three axes, in ONE place.
 *
 * A MERGED linked pull request wins over everything (#4260): the change shipped,
 * whatever the task row says. A task can fail AFTER its PR merged, or a human can
 * merge the PR of a task that died — prod, 30 days to 2026-09-16, 18 failed tasks
 * with a merged PR still read `failed`. The settled-PR sweep now moves such a row to
 * `merged` itself (#4022), keeping its failure columns; until it runs, this
 * precedence reads it merged. Either way the attempt's failure is not lost: the
 * `merged` arm carries it as `priorFailure`, history rather than headline.
 *
 * Otherwise an open blocker wins over every status-derived phase EXCEPT the two
 * terminals a human answer can no longer change: a `merged` task is merged
 * whatever ask is still open beside it, and a `cancelled` task is work nobody
 * wants resumed. On `failed` the blocker WINS — the run died at the wall it asked
 * about, and the answer is what moves it (triage mints new work from the ask plus
 * the answer, `entities/blocker.ts`).
 */
export function resolveTaskOutcomePhase(
  status: TaskStatus,
  hasOpenBlocker: boolean,
  pullRequestState: PullRequestState | null,
): TaskOutcomePhase {
  if (pullRequestState === 'merged') return TaskOutcomePhase.Merged;
  const phase = taskOutcomePhase(status);
  if (!hasOpenBlocker) return phase;
  if (phase === TaskOutcomePhase.Merged || phase === TaskOutcomePhase.Cancelled) return phase;
  return TaskOutcomePhase.Blocked;
}

/** The immutable per-PR page on GitHub. Not stored; minted from the two facts that are. */
export function pullRequestUrl(repoFullName: string, number: number): string {
  return `https://github.com/${repoFullName}/pull/${number}`;
}

/**
 * The `pull_requests` projection row this task is linked to
 * (`tasks.pull_request_number` on the task's own repo), as far as the babysit
 * projection has observed it. `reviewVerdict` is OUR reviewer's latest
 * `review_posted` verdict pinned to `headSha` (`approve` / `comment` /
 * `request_changes`, the `@developerz/review` vocabulary as a string); null is
 * "no platform verdict for this head" — a re-push earns a fresh review, never an
 * inherited pass.
 */
export type TaskOutcomePullRequest = {
  readonly number: number;
  readonly url: string;
  readonly title: string;
  readonly state: PullRequestState;
  readonly headSha: string | null;
  readonly ciStatus: CiStatus;
  /** GitHub's tri-state; null when the projection never saw it. */
  readonly mergeable: 'mergeable' | 'conflicting' | 'unknown' | null;
  /**
   * GITHUB's own refusal, when it has one (#4321) — the `mergeStateStatus` value that
   * means it will not take a merge PUT, and one sentence naming who holds it. Null when
   * GitHub is not refusing, or has never said.
   *
   * SEPARATE FROM {@link mergeable}, and from {@link mergeDecision}, because all three are
   * different gates and only one of them is ours. `mergeDecision` is the MAINTAINER's
   * recorded ask (`auto` / `ask` / `block`, null = none ever raised, which PASSES);
   * `mergeable` is GitHub's conflict test, whose `unknown` covers both "not computed yet"
   * and — once folded — "refused". This is GitHub's gate, stated as itself: on
   * tesote/tesote.ai#2481 every gate the platform grades was green and a caller reading
   * `mergeable: unknown` beside `mergeDecision: null` had no way to learn that GitHub was
   * refusing the merge over four un-dismissed CodeRabbit reviews on stale commits.
   */
  readonly mergeBlock: { readonly state: string; readonly reason: string } | null;
  readonly mergeDecision: MergeDecision | null;
  /**
   * `pull_requests.merge_decision_head_sha` — the head an `auto` approved (plan 101 M2). The
   * merge gate passes an `auto` only on that head, so a reader asking whether the CURRENT
   * decision passes needs it beside {@link headSha} (#4600).
   */
  readonly mergeDecisionHeadSha: string | null;
  readonly reviewVerdict: string | null;
  readonly mergedAt: Date | null;
  /**
   * #5453: the babysit driver's last `hop` reason for this pull request — what is
   * actually holding the merge from the platform's side. Null when the driver has not
   * yet written a hop for this PR, or the projection cannot prove one. `stale` is true
   * when the hop's `headSha` is not the PR's `headSha` (a hop written for a
   * now-superseded head); the wire renders "for superseded head <sha7>" rather than
   * the reason. `executed` mirrors `payload ->> 'executed'` cast to boolean (false when
   * absent, since the driver does not always write it).
   */
  readonly babysitHold: {
    readonly reason: string;
    readonly action: string | null;
    readonly executed: boolean;
    readonly headSha: string;
    readonly at: Date;
    readonly stale: boolean;
  } | null;
  /**
   * #4600: the merge-gate refusal the SAME newest hop recorded (`output.mergeRefusal`, a
   * `BabysitMergeRefusalCode` as a string, so a code a newer egress emits still reads, and
   * `output.mergeRefusalDetail`). Null when that hop recorded none: the newest hop is what
   * holds the PR now, and a refusal a later hop moved past is not current. Like
   * {@link babysitHold} it is head-agnostic — a moved head's newest hop is still the latest
   * word. `detail` is null when the row carries no detail string.
   */
  readonly mergeRefusal: { readonly code: string; readonly detail: string | null } | null;
};

/**
 * The CURRENT run (`tasks.run_id`) as its durable event stream shows it.
 * `runId` is null for a claimed task whose box has not streamed its first event
 * yet — the `runForTask` `{ runId: null }` state, one a caller polls THROUGH.
 */
export type TaskOutcomeRun = {
  readonly runId: string | null;
  readonly runnerId: string | null;
  readonly runnerDisplayName: string | null;
  /** `ts` of the run's first event. */
  readonly startedAt: Date | null;
  /** `ts` and `kind` of the run's newest event — the progress pulse `task run_status` served. */
  readonly lastEventAt: Date | null;
  readonly lastEventKind: string | null;
};

/**
 * WHERE A MERGED TASK'S CHANGE IS, past the merge. The owner's bar is "done when it is
 * in production, not when its PR merged", and the one read that answers it used to be
 * `deploy: <newest success> | null` — a null that collapsed four different facts:
 *
 *   `deployed`    — a SUCCESS verdict exists for the PR's merge commit (the sha a
 *                   Deployment targets; the join lives in `packages/db`
 *                   `task-outcome.ts`). The change is proven live.
 *   `failed`      — verdicts exist for the merge commit and in NO environment is the
 *                   newest one a success (a rollback reads here): the newest failure,
 *                   with GitHub's `description` of what failed. It read "Done — merged,
 *                   no deploy verdict recorded" before this — prod 2026-09-27, 9 of 387
 *                   merges to developerz-ai/developerz.ai in 7 days.
 *   `pending`     — the repo reports deploys, none has concluded for this merge commit,
 *                   and the merge is younger than `DEPLOY_VERDICT_GRACE_MINUTES` (`value-objects/deploy-verdict.ts`).
 *                   Something more WILL happen: this is the one merged state that is
 *                   not terminal.
 *   `missing`     — the repo reports deploys, but none concluded for this merge commit
 *                   within the grace (or the PR carries no merge commit to join on). A
 *                   repo that deploys only SOME merges lands here, and so does a broken
 *                   chain — the hourly gap alert (`deploy-verdict-gap.ts`) names those.
 *   `not_tracked` — the account has never recorded a verdict for this repo: nothing
 *                   reports its deploys to the platform, so merged is as far as it can
 *                   see. Prod 2026-09-27: all 188 merges to tesote/tesote.ai in 7 days.
 *
 * "The repo reports deploys" is read as: at least one verdict exists for
 * `(account, repo)`. Pure data here; the SQL that derives it is in `packages/db`.
 */
export const TaskOutcomeDeployState = {
  Deployed: 'deployed',
  Failed: 'failed',
  Pending: 'pending',
  Missing: 'missing',
  NotTracked: 'not_tracked',
} as const;
export const TaskOutcomeDeployStateSchema = z.enum(TaskOutcomeDeployState);
export type TaskOutcomeDeployState = z.infer<typeof TaskOutcomeDeployStateSchema>;

export type TaskOutcomeDeploy =
  | {
      readonly state: typeof TaskOutcomeDeployState.Deployed;
      readonly source: DeployVerdictSource;
      readonly environment: string | null;
      readonly recordedAt: Date;
    }
  | {
      readonly state: typeof TaskOutcomeDeployState.Failed;
      readonly environment: string | null;
      /** `deployment_status.description`, verbatim — what failing meant. */
      readonly description: string | null;
      readonly recordedAt: Date;
    }
  | { readonly state: typeof TaskOutcomeDeployState.Pending }
  | { readonly state: typeof TaskOutcomeDeployState.Missing }
  | { readonly state: typeof TaskOutcomeDeployState.NotTracked };

/** The facts {@link resolveTaskOutcomeDeploy} folds, as the outcome read serves them. */
export type TaskOutcomeDeployFacts = {
  /**
   * The merge commit's best verdict, per environment: each environment's NEWEST verdict
   * stands for it, then a success among those, else the newest failure. A commit whose
   * newest verdict in its one environment is a failure (a rollback) arrives as that
   * failure.
   */
  readonly verdict: {
    readonly source: DeployVerdictSource;
    readonly conclusion: 'success' | 'failure';
    readonly environment: string | null;
    readonly description: string | null;
    readonly recordedAt: Date;
  } | null;
  /** At least one verdict exists for this task's `(account, repo)`. */
  readonly repoReportsDeploys: boolean;
  /** The merge is older than `DEPLOY_VERDICT_GRACE_MINUTES` (`value-objects/deploy-verdict.ts`), or has no merge time. */
  readonly graceElapsed: boolean;
};

/** The deploy state of a merged task. Pure; total over its facts. */
export function resolveTaskOutcomeDeploy(facts: TaskOutcomeDeployFacts): TaskOutcomeDeploy {
  const v = facts.verdict;
  if (v !== null && v.conclusion === 'success') {
    return {
      state: TaskOutcomeDeployState.Deployed,
      source: v.source,
      environment: v.environment,
      recordedAt: v.recordedAt,
    };
  }
  if (v !== null) {
    return {
      state: TaskOutcomeDeployState.Failed,
      environment: v.environment,
      description: v.description,
      recordedAt: v.recordedAt,
    };
  }
  if (!facts.repoReportsDeploys) return { state: TaskOutcomeDeployState.NotTracked };
  return facts.graceElapsed
    ? { state: TaskOutcomeDeployState.Missing }
    : { state: TaskOutcomeDeployState.Pending };
}

export type TaskOutcomeFailure = {
  /** `tasks.failure_kind` — the LAST failed attempt's kind. */
  readonly kind: TaskFailureKind | null;
  /**
   * WHY, in words. The current run's newest `error` event's `message` when a box wrote
   * one; otherwise the kind's own sentence (`taskFailureSentence`) — a failure a
   * PLATFORM lane wrote (a lost box, a dead dependency) has no box error to quote, and
   * an empty reason there read as "nothing is known" when the kind knew exactly what
   * happened (`tsk_4b4aadc3…`, 2026-09-27). Null only on a row with no stamped kind.
   */
  readonly reason: string | null;
  readonly attempt: number;
  /** `tasks.run_id` — the run the failure ended, for `task run_tail`; null when none started. */
  readonly runId: string | null;
};

/**
 * THE ATTEMPT BEFORE THIS QUEUED ONE — a task that ran, failed and was put back in the
 * queue (a `resume_backoff` park, a retry, an operator redispatch). Every one of those
 * writers clears `tasks.run_id`, and every run read here keyed on that column, so a
 * re-queued task answered `run: null, failure: null`: the run that just failed and the
 * words it failed with were unreachable without database access (tesote/tesote.ai
 * `tsk_98ceab62…`, 2026-09-30: parked on `resume_backoff` after its 11th run, the
 * outcome naming neither that run nor its `verify_failed` tail).
 *
 * `runId` is the task's NEWEST run by its own events, so `task run_tail` replays it.
 * `failure` is null when the row carries no `failure_kind`; its `kind` is that column
 * (the last FAILED attempt's, which is the stamp `task list` shows) and its `reason` is
 * THIS run's newest `error` message, else the kind's sentence. `model` is this run's
 * newest `run_provenance`, null when it published none.
 */
export type TaskOutcomeLastAttempt = {
  readonly runId: string;
  /** `ts` and `kind` of that run's newest event. */
  readonly endedAt: Date | null;
  readonly lastEventKind: string | null;
  readonly failure: TaskOutcomeFailure | null;
  readonly model: {
    readonly model: string | null;
    readonly servedModel: string | null;
    readonly provider: string | null;
  } | null;
};

export type TaskOutcomeBlocker = {
  readonly id: BlockerId;
  readonly kind: BlockerKind;
  readonly summary: string;
  readonly askedAt: Date;
  /**
   * WHAT MOVES THE TASK. `answer` for every blocker a run raised: the answer files the
   * resumption. `task_redispatch` for the blocker a HOLD raised (`./env-fault-hold`,
   * 2026-09-29): the task is held on a box fault, and answering would file NEW work while
   * the held task sat on — the release is `task redispatch` once the box is fixed.
   */
  readonly release: 'answer' | 'task_redispatch';
};

/**
 * WHICH MODEL RAN IT (2026-09-24, the model escalation ladder) — the CURRENT run's own
 * `run_provenance` (the model it asked for, the one the provider served, which seam
 * resolved it), plus the ladder's newest ESCALATION of this task, if any.
 *
 * The run's provenance is the evidence and the escalation row is the reason: a run on
 * `glm-5.3` with `resolvedBy: 'route'` reads the same whether a coding route or the
 * escalation ladder picked it, so the two ride side by side rather than one being
 * inferred from the other.
 */
export type TaskOutcomeModel = {
  /** The model the current run ASKED for; null before its box published provenance. */
  readonly model: string | null;
  /** The model the provider actually SERVED (`run_provenance.served_model`), when seen. */
  readonly servedModel: string | null;
  readonly provider: string | null;
  /** `LlmTierSource` as a string — which rung resolved the credential; null on a box-env run. */
  readonly resolvedBy: string | null;
  /** The newest `dispatch.model_escalated` escalation of this task; null when it never climbed. */
  readonly escalation: {
    readonly fromModel: string;
    readonly toModel: string;
    /** `route` (the account's `escalation` route) or `tier` (a stronger tag in its pool). */
    readonly via: string;
    readonly at: Date;
  } | null;
};

/**
 * WHAT REPLACED THIS TASK (`tasks.superseded_by`). `split`: babysit stopped its pull request
 * (`ci_not_converging`), triage cut the split, and the follow-through regrouped every plan slot
 * onto these tasks, cancelled this one `superseded_by_split` and closed the PR. `regroup`: an
 * operator's `plan regroup` (or its follow-through) put them in its slot. Either way nothing
 * waits on this task any more — the ids are the work to follow. Without it, a superseded task
 * read as a plain `cancelled` beside an idle PR, with nothing naming where its work went
 * (tesote/tesote.ai#2696, 2026-09-27: five hours before anyone asked).
 */
export type TaskOutcomeSupersession = {
  readonly taskIds: readonly TaskId[];
  readonly via: 'split' | 'regroup';
};

/**
 * ONE `depends_on` target that has NOT released this task — the claim's own rule
 * (`taskReleasesDependentsSql`, `packages/db` `queries/tasks/gates.ts`): merged, or
 * finished with no pull request. A pending row with no hold stamped can still be
 * unclaimable on exactly this gate, and no hold writer stamps it: prod 2026-09-28
 * (tesote), 6 of 6 hold-less pending tasks waited on a dependency while 8+ boxes sat
 * idle, and the outcome read "waiting for a box … nothing is wrong".
 */
export type TaskOutcomeDependency = {
  /** The id as `depends_on` stores it. */
  readonly taskId: string;
  /** The target's status; null when this account owns no such task (missing, or another tenant's). */
  readonly status: TaskStatus | null;
  /**
   * The target itself waits — along unreleased `depends_on` edges, within the account —
   * on THIS task: a dependency cycle no claim can ever break (tsk_1be837ae… <->
   * tsk_faa4cbef…, 2026-09-28).
   */
  readonly waitsOnThisTask: boolean;
  /**
   * A regroup or split SUPERSEDED the target (`superseded_by` set): it reads `cancelled`
   * but is not dead. The dead-dependency lane's heal pass (`healSupersededDependencies`)
   * rewrites the edge onto its replacements, and the fail pass never counts it — the
   * same exclusion `task stalls`' `dependency_failed` makes.
   */
  readonly superseded: boolean;
};

/**
 * WHY a delivered run has no `planId` — the three causes a bare `null` collapsed (#6501
 * A3). Measured prod 2026-10-19, every `completed` scout task: 815 with a plan, 129
 * `findings_only`, 160 `not_recorded`; every non-planning origin is `not_a_planner`.
 *
 *   `not_a_planner`  the origin's run never authors a plan ({@link ORIGIN_RUN_AUTHORS_PLAN}
 *                    false) — a coding, review or devops task. Expected; nothing to chase.
 *   `findings_only`  a `return_context: true` planning run: it answers with findings and
 *                    mints NOTHING by design — the caller writes the plan itself.
 *   `not_recorded`   a planning run on the minting arm whose plan has no `plans` row: not
 *                    ingested yet, every group refused or empty, or the ingest failed.
 *                    The one cause worth chasing (the run's `plan_authored` event and its
 *                    audit say which).
 */
export const TaskOutcomePlanAbsence = {
  NotAPlanner: 'not_a_planner',
  FindingsOnly: 'findings_only',
  NotRecorded: 'not_recorded',
} as const;
export const TaskOutcomePlanAbsenceSchema = z.enum(TaskOutcomePlanAbsence);
export type TaskOutcomePlanAbsence = z.infer<typeof TaskOutcomePlanAbsenceSchema>;

/**
 * The {@link TaskOutcomePlanAbsence} of a delivered run — `null` EXACTLY when `planId` is
 * non-null, so the two fields can never both speak or both be silent.
 */
export function resolveTaskOutcomePlanAbsence(facts: {
  readonly planId: string | null;
  readonly origin: TaskOrigin;
  /** The envelope's `return_context` (absent reads false). */
  readonly returnContext: boolean;
}): TaskOutcomePlanAbsence | null {
  if (facts.planId !== null) return null;
  if (!ORIGIN_RUN_AUTHORS_PLAN[facts.origin]) return TaskOutcomePlanAbsence.NotAPlanner;
  return facts.returnContext
    ? TaskOutcomePlanAbsence.FindingsOnly
    : TaskOutcomePlanAbsence.NotRecorded;
}

/**
 * Where a task stands in its account's claim queue — `taskQueuePosition`
 * (`packages/db` `queries/tasks/queue-position.ts`). Discriminated so "not queued",
 * "held" and "first in line" are never one number.
 *
 *   `queued`       pending and unheld. `ahead` counts the account's pending rows a box could
 *                  claim right now (every claimant-independent claim gate passes, and some
 *                  live box could place it) that the claim's own ORDER BY sorts before this
 *                  one. `claimableNow` is the same test on THIS row: false means the row
 *                  waits on a gate (dependency, plan DAG, area, onboarding, mode, no live
 *                  box) and `ahead` is where it would stand once that lifts.
 *   `held`         pending with a hold stamped (`TaskHoldReason`, as `normalizeTask` derives
 *                  it); `until` is the window's end when the hold has one. A window that
 *                  has LAPSED is not a hold — the claim reaches the row again — so it
 *                  reads `queued`.
 *   `not_pending`  claimed, running or finished — no queue position exists.
 *   `not_found`    the account owns no such task (never confirms another tenant's).
 */
export type TaskQueuePosition =
  | { readonly kind: 'queued'; readonly ahead: number; readonly claimableNow: boolean }
  | { readonly kind: 'held'; readonly reason: TaskHoldReason; readonly until: Date | null }
  | { readonly kind: 'not_pending'; readonly status: TaskStatus }
  | { readonly kind: 'not_found' };

type TaskOutcomeBase = {
  readonly taskId: TaskId;
  /** The durable status — the authority `phase` is projected from. */
  readonly status: TaskStatus;
  readonly outcomeKind: OutcomeKind;
  /**
   * Who asked for this task (`envelope.origin`). `outcomeKind` alone cannot say
   * what "done" means next: a `scout` and a `devops` run both deliver a `report`,
   * but only the scout's report is a PLAN whose own tasks carry the pull
   * requests — so a client told "delivered" on the task `plan_start` returned has
   * to be sent to those tasks, not told the change shipped.
   */
  readonly origin: TaskOrigin;
  readonly repoFullName: string | null;
  readonly title: string | null;
  /** The linked PR projection, when the task has one — a `pr_babysit` row carries it while still queued. */
  readonly pullRequest: TaskOutcomePullRequest | null;
  /** Which model ran the current attempt and whether the escalation ladder chose it; null when neither is known. */
  readonly model: TaskOutcomeModel | null;
  /** The tasks that replace this one; null when nothing superseded it. */
  readonly supersededBy: TaskOutcomeSupersession | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

/**
 * The one-read answer. Discriminated on `phase`; every arm carries the base
 * facts and only the facts its phase can have, so a consumer never reads a
 * `queuePosition` off a merged task or a `failure` off a queued one.
 */
export type TaskOutcome = TaskOutcomeBase &
  (
    | {
        readonly phase: typeof TaskOutcomePhase.Queued;
        readonly holdReason: TaskHoldReason | null;
        /** 1-based position in the account's claim order; `queueDepth` is the account's pending count. */
        readonly queuePosition: number;
        readonly queueDepth: number;
        /**
         * The `depends_on` targets that have not released it, in `depends_on` order; empty
         * when none holds it. See {@link TaskOutcomeDependency}.
         */
        readonly waitingOn: readonly TaskOutcomeDependency[];
        /** The run before this wait, when the task has run at all. See {@link TaskOutcomeLastAttempt}. */
        readonly lastAttempt: TaskOutcomeLastAttempt | null;
      }
    | { readonly phase: typeof TaskOutcomePhase.Running; readonly run: TaskOutcomeRun }
    | {
        readonly phase: typeof TaskOutcomePhase.Delivered;
        readonly run: TaskOutcomeRun;
        /**
         * The plan this run AUTHORED, read back from `plans` — never derived
         * from the run id. A scout plan's id IS its run id (`insertPlanGroups`
         * mints the parent row under it), which reads as "the run id is the
         * plan id" until you meet the session that authors NO plan: a
         * `return_context: true` ask answers with findings and mints nothing,
         * and a derivation published an id whose `plan get` finds no row.
         * `null` on every run that authored none — every non-scout task, and a
         * scout whose plan is not ingested (yet).
         */
        readonly planId: string | null;
        /** Why `planId` is null; null exactly when it is not ({@link resolveTaskOutcomePlanAbsence}). */
        readonly planAbsence: TaskOutcomePlanAbsence | null;
      }
    | { readonly phase: typeof TaskOutcomePhase.AwaitingCi }
    | { readonly phase: typeof TaskOutcomePhase.InReview }
    | { readonly phase: typeof TaskOutcomePhase.AwaitingMerge }
    | {
        readonly phase: typeof TaskOutcomePhase.Merged;
        readonly deploy: TaskOutcomeDeploy;
        /**
         * The failure of the row's OWN run, kept as history: non-null when the row is
         * `failed` but its PR merged anyway ({@link resolveTaskOutcomePhase}), or it
         * reads `merged` and its current run ended in an `error` event — the settled-PR
         * sweep's `failed → merged` (#4022), which keeps `run_id`. NOT the
         * `failure_kind` stamp alone: a re-pend keeps the stamp but clears `run_id`, so
         * on a row a later attempt landed the stamp names an earlier run, and this
         * would pair it with the merging run's attempt and id. Null otherwise.
         */
        readonly priorFailure: TaskOutcomeFailure | null;
      }
    | { readonly phase: typeof TaskOutcomePhase.Failed; readonly failure: TaskOutcomeFailure }
    | { readonly phase: typeof TaskOutcomePhase.Cancelled }
    | { readonly phase: typeof TaskOutcomePhase.Blocked; readonly blocker: TaskOutcomeBlocker }
  );
